import { useCallback, useEffect, useState } from "react";

import { useDropzone } from "react-dropzone";

import dayjs from "dayjs";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Upload,
  X,
} from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useImportSupplierInvoices } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import {
  type BsaleParseResult,
  type ParsedBsaleInvoice,
  parseBsaleDteCompra,
} from "~/lib/purchasing/parseBsaleDteCompra";
import { cn } from "~/lib/utils";
import { supplierInvoiceService } from "~/services/purchasing";

import type { SupplierInvoiceImportRow } from "~/types/purchasing";

// AP go-live is 01/10/2026 — everything before was wiped, so only load from 01/10 onward.
const CUTOFF = "2026-10-01";

const REASON_LABELS: Record<string, string> = {
  din: "Declaración de Ingreso (DIN)",
  pre_cutoff: "Anteriores al 01/10",
  tipo_desconocido: "Tipo de documento no reconocido",
};

const money = (n: number) =>
  `$${new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(n)}`;

const keyOf = (i: { rut: string; doc_tipo_code: string; folio: string }) =>
  `${i.rut}|${i.doc_tipo_code}|${i.folio}`;

function toImportRow(inv: ParsedBsaleInvoice): SupplierInvoiceImportRow {
  return {
    rut: inv.rut,
    razon_social: inv.razon_social,
    doc_tipo_code: inv.doc_tipo_code,
    document_type: inv.document_type,
    folio: inv.folio,
    emission_date: inv.emission_date,
    due_date: dayjs(inv.emission_date).add(30, "day").format("YYYY-MM-DD"),
    due_date_estimated: true,
    bsale_estado: inv.bsale_estado,
    // AP sign convention: facturas negative, NC positive. The parser keeps the export's native
    // signs (factura +, NC −), so negate uniformly here when mapping to the stored row.
    exento: -inv.exento,
    neto: -inv.neto,
    iva: -inv.iva,
    impto_especifico: -inv.impto_especifico,
    total_clp: -inv.total,
    saldo_clp: -inv.total,
  };
}

interface Preview {
  parse: BsaleParseResult;
  nuevas: SupplierInvoiceImportRow[];
  nuevasMonto: number;
  yaCargadas: number;
  yaCargadasMonto: number;
  excluded: { reason: string; count: number; monto: number }[];
}

interface SupplierInvoiceImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupplierInvoiceImportDialog({
  open,
  onOpenChange,
}: SupplierInvoiceImportDialogProps) {
  const { toast } = useToast();
  const importMutation = useImportSupplierInvoices();
  const { data: companies = [] } = useCompanies();
  const [companyId, setCompanyId] = useState<string>(ALIACE_SPA_ID);
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setError(null);
      setCompanyId(ALIACE_SPA_ID);
    }
  }, [open]);

  const handleParse = useCallback(async (uploaded: File) => {
    setIsParsing(true);
    setError(null);
    setPreview(null);
    try {
      const html = await uploaded.text();
      const parse = parseBsaleDteCompra(html, { cutoff: CUTOFF });
      if (parse.invoices.length === 0 && parse.excluded.length === 0) {
        setError(
          "No se reconocieron facturas. Verifica que sea el export 'DTE de Compra' de BSale (Documentos → Recepcionados → Exportar a Excel)."
        );
        return;
      }
      const existing = new Set(
        (await supplierInvoiceService.checkExisting(
          parse.invoices.map((i) => ({
            rut: i.rut,
            doc_tipo_code: i.doc_tipo_code,
            folio: i.folio,
          }))
        )).map(keyOf)
      );

      const nuevasInv = parse.invoices.filter((i) => !existing.has(keyOf(i)));
      const yaInv = parse.invoices.filter((i) => existing.has(keyOf(i)));
      const excludedMap = new Map<string, { count: number; monto: number }>();
      for (const e of parse.excluded) {
        const g = excludedMap.get(e.reason) ?? { count: 0, monto: 0 };
        g.count += 1;
        g.monto += e.total;
        excludedMap.set(e.reason, g);
      }

      setPreview({
        parse,
        nuevas: nuevasInv.map(toImportRow),
        nuevasMonto: nuevasInv.reduce((s, i) => s + i.total, 0),
        yaCargadas: yaInv.length,
        yaCargadasMonto: yaInv.reduce((s, i) => s + i.total, 0),
        excluded: [...excludedMap.entries()].map(([reason, v]) => ({ reason, ...v })),
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsParsing(false);
    }
  }, []);

  const onDrop = useCallback(
    (accepted: File[]) => {
      const f = accepted[0];
      if (f) {
        setFile(f);
        void handleParse(f);
      }
    },
    [handleParse]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "text/html": [".html", ".htm"],
    },
    maxFiles: 1,
  });

  const reconciles = preview?.parse.reconciles ?? false;
  const canConfirm = !!preview && reconciles && preview.nuevas.length > 0;

  const handleConfirm = () => {
    if (!preview) return;
    importMutation.mutate({ rows: preview.nuevas, companyId }, {
      onSuccess: (res) => {
        toast({
          title: "Facturas cargadas",
          description: `${res.inserted} nuevas · ${res.skipped} ya existían · ${res.suppliers_created} proveedores creados`,
        });
        onOpenChange(false);
      },
      onError: (e) =>
        toast({ title: "Error", description: e.message, variant: "destructive" }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Cargar facturas de proveedor (BSale)</DialogTitle>
          <DialogDescription>
            Suba el export <strong>DTE de Compra</strong> de BSale (Documentos → Recepcionados →
            Exportar a Excel). Se muestra una vista previa antes de guardar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="import-empresa">Empresa</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger id="import-empresa" className="h-9">
                <SelectValue placeholder="Seleccione una empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Las facturas se cargarán en esta empresa (BSale no indica la empresa).
            </p>
          </div>

          {!file ? (
            <div
              {...getRootProps()}
              className={cn(
                "cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors",
                isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50"
              )}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                {isDragActive
                  ? "Suelte el archivo aquí..."
                  : "Arrastre el archivo aquí o haga clic para seleccionar"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Formato: .xls de BSale</p>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-xl border bg-muted/50 px-4 py-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                  setError(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {isParsing && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm text-muted-foreground">Procesando archivo...</span>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {preview && (
            <div className="space-y-3">
              {/* reconciliation */}
              {reconciles ? (
                <Alert className="border-green-200 bg-green-50 text-green-800">
                  <CheckCircle2 className="h-4 w-4 !text-green-600" />
                  <AlertDescription>
                    Cuadra con los totales de BSale ✓
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No cuadra con los totales de BSale — revisar antes de cargar.
                  </AlertDescription>
                </Alert>
              )}

              {/* breakdown */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">Nuevas (se cargan)</p>
                  <p className="text-lg font-bold text-green-700">
                    {preview.nuevas.length}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {money(preview.nuevasMonto)}
                  </p>
                </div>
                <div className="rounded-lg border bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">Ya cargadas (se omiten)</p>
                  <p className="text-lg font-bold">{preview.yaCargadas}</p>
                  <p className="text-xs text-muted-foreground">
                    {money(preview.yaCargadasMonto)}
                  </p>
                </div>
              </div>

              {preview.excluded.length > 0 && (
                <div className="rounded-lg border p-3 text-sm">
                  <p className="mb-1 font-medium text-gray-700">Excluidas</p>
                  <ul className="space-y-0.5 text-muted-foreground">
                    {preview.excluded.map((e) => (
                      <li key={e.reason} className="flex justify-between">
                        <span>{REASON_LABELS[e.reason] ?? e.reason}</span>
                        <span>
                          {e.count} · {money(e.monto)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.parse.warnings.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {preview.parse.warnings.slice(0, 3).join(" · ")}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              disabled={!canConfirm || importMutation.isPending}
              onClick={handleConfirm}
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cargando...
                </>
              ) : (
                `Cargar ${preview?.nuevas.length ?? 0} facturas`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
